import { Building2, Target, Users, Award, MapPin, Calendar } from "lucide-react";

const About = () => {
  return (
    <div className="w-full" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-orange-500 text-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Maruns Limited</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            UK-based logistics and transport provider delivering reliable services across London and the wider UK
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">About Maruns Limited</h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Maruns Limited is a UK-based logistics and transport provider delivering reliable and efficient services to both residential and commercial clients.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We specialize in home removals, business logistics, and the secure transport of equipment and materials. Our operations are focused on providing timely, safe, and compliant transport solutions across London and the wider UK.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              As a growing company, we are committed to maintaining high standards of service, safety, and operational efficiency. We aim to expand our operations and workforce to meet increasing demand across the UK, supporting a wide range of industries with flexible logistics solutions tailored to client needs.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
              <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
                <div className="flex items-center mb-4">
                  <Calendar className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-bold text-gray-900">Established</h3>
                </div>
                <p className="text-gray-700">September 2025</p>
              </div>

              <div className="bg-orange-50 p-6 rounded-xl border-2 border-orange-200">
                <div className="flex items-center mb-4">
                  <MapPin className="w-8 h-8 text-orange-600 mr-3" />
                  <h3 className="text-xl font-bold text-gray-900">Location</h3>
                </div>
                <p className="text-gray-700">Based in Luton, Operating across London & UK</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6 mt-12">Our Mission</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              To deliver efficient and dependable logistics services while maintaining the highest standards of safety and compliance. We support both individuals and businesses with reliable, insured, and time-sensitive transport solutions.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">What We Specialize In</h2>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <span className="text-gray-700">Residential house and apartment removals across the UK</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <span className="text-gray-700">Commercial and business logistics for operational efficiency</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <span className="text-gray-700">Daily supplies delivery services including newspapers, milk, tissues, and paper rolls</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <span className="text-gray-700">Specialized transport of equipment, technical materials, and high-value items</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-3">✓</span>
                <span className="text-gray-700">Light clearance and removal support as part of removals and transport services. For regulated waste disposal, we work with licensed partners where required</span>
              </li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">Industries We Support</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              We provide ongoing logistics support to businesses and households across the UK, including:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Construction Companies</p>
                <p className="text-sm text-gray-600">Equipment and materials transport</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Event & Catering Businesses</p>
                <p className="text-sm text-gray-600">Equipment logistics and delivery</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Offices & Retail Operations</p>
                <p className="text-sm text-gray-600">Commercial transport solutions</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Residential Clients</p>
                <p className="text-sm text-gray-600">House moves and personal logistics</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We operate across London and surrounding areas, with coverage extending throughout the UK. Our team is available for both scheduled and urgent transport jobs, maintaining full licensing, comprehensive insurance, and strict adherence to UK transport regulations.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed">
              As we continue to grow and expand our services, we remain focused on delivering professional, reliable, and customer-focused logistics solutions that businesses and individuals can trust. We handle residential moves, commercial transport, and specialized equipment logistics with the same level of care and professionalism.
            </p>
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Registered Company Name</h3>
                <p className="text-gray-700">Maruns Limited</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Registered in</h3>
                <p className="text-gray-700">England & Wales</p>
              </div>
              <div className="md:col-span-2">
                <h3 className="font-semibold text-gray-900 mb-2">Registered Office</h3>
                <p className="text-gray-700">
                  Innovation Centre and Business Base<br />
                  110 Butterfield, Great Marlings<br />
                  Luton, LU2 8DL<br />
                  United Kingdom
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Contact</h3>
                <p className="text-gray-700">
                  Phone: +44 7405 747177<br />
                  Email: marunsltd@outlook.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
